import React, { useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useSessionCleanup, useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import Preview from '@renderer/components/Preview'
import BkScreen from './BkScreen'
import { useBkPageTimeout } from './pageTimeout'
import BkTimer from './BkTimer'
import { BK_BIG_FRAME, BK_HIT, bkHit, stripBox } from './layout'
import { BK_CAPTURE_RATIO, orderedSlots } from './cutCategories'
import { paysFirst } from './flow'
import { useBkCapture } from './useBkCapture'
import { cssStageOf } from './filterPreview'
import BkPixel from './BkPixel'
import { BK_WORD_RETAKE } from './lettering'
import { bkUploadContext, startBkPhotoUpload, type UserContext } from './mediaUpload'

/**
 * Screen 5 — "look at the camera".
 *
 * Live view in the painted gold frame, the strip filling up on the left in the
 * frame's OWN slot layout, and the retake / confirm swans.
 *
 * Tapping a filled slot makes it active: the big frame shows that photograph
 * instead of the live view, and the retake swan retakes that one. Tapping it
 * again, or finishing, returns the frame to the live camera.
 */
const Capture: React.FC = () => {
  const navigate = useNavigate()
  const sessionCleanup = useSessionCleanup()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const { downloadLocal } = useSettingsStore()
  const { logActivity, uploadSessionState, userPreferences } = useSessionStateStore()
  const capturedItems = useSessionStateStore((s) => s.gallery?.capturedItems)
  const { filters, frames, config } = useConfigStore()

  const { phase, tick, retakeMany, retakingIndex, pendingRetakes, canRetake, error, retryShutter } =
    useBkCapture()

  /** Slots the guest has marked for retaking. One swan, so it takes a set. */
  const [selected, setSelected] = useState<Set<number>>(new Set())
  // Choosing which photographs to replace is a decision about photographs that
  // exist; offering it mid-countdown invites a tap that lands on the next shot.
  const busy = phase === 'prep' || phase === 'counting' || phase === 'shooting'

  const selectedFilter = filters.find((f) => f._id === userPreferences?.filterId)
  /**
   * Each strip photo in the chosen filter. The bake lands a moment after the
   * shutter; until it does, the raw shot gets the bake's own CSS stage, so it
   * never shows in colour under a mono live view.
   */
  const baked = (item: { filteredVersions?: Record<string, string> }): string | undefined =>
    selectedFilter ? item.filteredVersions?.[selectedFilter._id] : undefined
  const unbakedFilter = cssStageOf(selectedFilter?.adjustments)
  const frame = frames.find((f) => f._id === userPreferences?.frameId)
  const shots = capturedItems ?? []

  /** The frame's real windows, so an 8-cut strip reads as two columns of four. */
  const slots = orderedSlots(frame)
  const canvasW = frame?.drawParams.canvasWidth ?? 600
  const canvasH = frame?.drawParams.canvasHeight ?? 2400
  const pct = (n: number, of: number): string => `${(n / of) * 100}%`

  const confirm = (): void => {
    logActivity(`BK capture confirmed with ${shots.length} photos`, { to: 'frame' })
    uploadSessionState()
    // The photographs are final from here, so they head for the gallery now
    // and are there by the time the QR is (see startBkPhotoUpload).
    void startBkPhotoUpload(bkUploadContext(context, projectId, downloadLocal))
    navigate(`/${projectId}/frame`)
  }

  const toggle = (i: number): void => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  }

  /**
   * Two stages, two settings — stock splits them across two pages and BK runs
   * both on this one. While shooting, captureTimeout; once every slot is
   * filled and the guest is reviewing, retakeTimeout.
   *
   * Shooting expiry does what stock FrameBasedCapture does: with photographs,
   * carry on; with none, send the guest back to choose again.
   */
  const captureRemaining = useBkPageTimeout(
    config?.pages?.capture?.settings?.captureTimeout,
    () => {
      if (shots.length > 0) {
        logActivity('BK capture timed out; continuing with the photos taken', {
          taken: shots.length
        })
        confirm()
      } else {
        // Back to a screen that comes after payment in this project's order
        // (flow.ts), never one that would charge again: the filter, or the cut
        // when the guest paid first. With neither, the guest has long gone —
        // end the session.
        const back = config?.pages?.filter?.settings?.enabled
          ? 'filter'
          : paysFirst(config?.pages)
            ? 'checkout'
            : null
        if (back) {
          logActivity(`BK capture timed out with no photos; back to ${back}`)
          navigate(`/${projectId}/${back}`)
        } else {
          logActivity('BK capture timed out with no photos; ending the session')
          void sessionCleanup('capture_timeout')
        }
      }
    },
    phase !== 'review'
  )
  const reviewRemaining = useBkPageTimeout(
    config?.pages?.retake?.settings?.retakeTimeout,
    confirm,
    phase === 'review'
  )

  const doRetake = (): void => {
    const targets = [...selected]
    if (targets.length === 0) return
    setSelected(new Set())
    retakeMany(targets)
  }

  return (
    <BkScreen plate="capture.webp">
      <div
        className="bk-flash pointer-events-none absolute inset-0 z-50 bg-white"
        style={{ display: 'none' }}
      />

      {/* Big frame: the live camera, or the slot the guest is inspecting. */}
      <div style={bkHit(BK_BIG_FRAME)} className="overflow-hidden">
        {
          <Preview
            cameraKey={(userPreferences?.selectedCamera as 'primary' | 'secondary') ?? 'primary'}
            ratio={BK_CAPTURE_RATIO}
            adjustments={selectedFilter?.adjustments}
            postLUTAdjustments={selectedFilter?.postLUTAdjustments}
            filterOverlay={selectedFilter?.overlay}
            filter={selectedFilter?._id}
            isPaused={phase === 'review'}
            fillContainer
          />
        }
      </div>

      {/* The strip, laid out exactly as it will print. */}
      <div style={bkHit(stripBox(canvasW, canvasH))}>
        <div className="relative h-full w-full">
          {slots.map((slot, i) => {
            const item = shots[i]
            const isSelected = selected.has(i)
            // Marked from the moment it is chosen until its new photograph
            // lands — not just until the swan is pressed. Clearing it on the
            // press left the guest unable to see which shots were being redone.
            const isMarked = isSelected || pendingRetakes.includes(i)
            return (
              <button
                key={`${slot.type}-${i}`}
                aria-label={
                  item
                    ? `Photo ${i + 1}${isSelected ? ', selected to retake' : ''}`
                    : `Photo ${i + 1} not taken yet`
                }
                aria-pressed={isSelected}
                disabled={!item || busy}
                onClick={() => toggle(i)}
                className={`absolute overflow-hidden bg-black/10 ${
                  isMarked ? 'ring-[5px] ring-[#6C1824]' : ''
                } ${retakingIndex === i ? 'ring-[5px] ring-white' : ''}`}
                style={{
                  left: pct(slot.left, canvasW),
                  top: pct(slot.top, canvasH),
                  width: pct(slot.width, canvasW),
                  height: pct(slot.height, canvasH)
                }}
              >
                {item?.photoUrl && (
                  <img
                    src={baked(item) ?? item.photoUrl}
                    alt=""
                    className="h-full w-full object-cover"
                    style={{
                      filter:
                        [
                          baked(item) ? undefined : unbakedFilter,
                          isMarked ? 'brightness(0.55)' : ''
                        ]
                          .filter(Boolean)
                          .join(' ') || undefined
                    }}
                  />
                )}
                {isMarked && (
                  // Their own "retake", from the retake swan, in the swan's white.
                  <span className="absolute inset-0 flex items-center justify-center">
                    <BkPixel rows={BK_WORD_RETAKE} block={1.4} color="#FFFAF9" />
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {phase !== 'review' && phase !== 'stalled' && (
        <div
          style={bkHit(BK_BIG_FRAME)}
          className="pointer-events-none flex flex-col items-center justify-center gap-4"
        >
          {phase === 'shooting' ? (
            <span className="text-[56px] text-white drop-shadow-[0_4px_18px_rgba(0,0,0,0.65)]">
              Taking the photo…
            </span>
          ) : (
            <span
              className="font-bold text-white drop-shadow-[0_4px_18px_rgba(0,0,0,0.65)]"
              style={{ fontSize: phase === 'prep' ? 180 : 260, lineHeight: 1 }}
            >
              {tick > 0 ? tick : ''}
            </span>
          )}
        </div>
      )}

      {error && phase !== 'stalled' && (
        <div
          style={bkHit({
            ...BK_BIG_FRAME,
            top: BK_BIG_FRAME.top + BK_BIG_FRAME.height - 92,
            height: 92
          })}
          className="pointer-events-none flex items-center justify-center bg-[#6C1824]/90 px-6"
        >
          <span className="text-center text-[26px] text-[#F0EEDC]">{error} — trying again</span>
        </div>
      )}

      {phase === 'stalled' && (
        <div
          style={bkHit(BK_BIG_FRAME)}
          className="flex flex-col items-center justify-center gap-6 bg-black/65 px-10 text-center"
        >
          <span className="text-[38px] text-white">The camera didn&apos;t take the photo</span>
          {error && <span className="text-[24px] text-white/80">{error}</span>}
          <button
            onClick={retryShutter}
            className="rounded-sm bg-[#F0EEDC] px-10 py-4 text-[28px] text-[#6C1824]"
          >
            Try again
          </button>
        </div>
      )}

      {/* Painted swans. The retake glyph marks which photo it will replace. */}
      <button
        aria-label={selected.size ? `Retake ${selected.size} photo(s)` : 'Retake'}
        disabled={selected.size === 0 || !canRetake || busy}
        onClick={doRetake}
        style={bkHit(BK_HIT.capture.retake)}
        className="cursor-pointer bg-transparent disabled:cursor-not-allowed disabled:opacity-40"
      />
      <button
        aria-label="Confirm"
        disabled={shots.length === 0 || busy}
        onClick={confirm}
        style={bkHit(BK_HIT.capture.confirm)}
        className="cursor-pointer bg-transparent disabled:cursor-not-allowed disabled:opacity-40"
      />
      <BkTimer seconds={reviewRemaining ?? captureRemaining} />
    </BkScreen>
  )
}

export default Capture
